import React, { ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import ConfigurableCmsComponentMetadata from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-component-metadata.component';
import ConfigurableCmsSlotMetadata from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-slot-metadata.component';
import ConfigurableCmsValidity from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-validity.component';
import HeadlineComponent from '../headline/headline.component';
import ImageComponent from '@spa/components/image-component/image.component';
import { LinkComponentLink } from '../link/link.component';
import { Metadata } from '@spa/pages-converters/cms/component-envelops/CmsComponentEnvelopBase';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import classNames from 'clsx';
import { flushSync } from 'react-dom';
import styles from './accordion.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';

export interface Entry {
  readonly title: string;
  readonly content: ReactNode;
  readonly isInitialOpened?: boolean;
  readonly headerAccessory?: ReactNode;
  readonly allowContentOverflow?: boolean;
  readonly anchorId?: string;
  readonly metadata: ReadonlyArray<Metadata>;
  readonly dateTo: number | null;
  readonly dateFrom: number | null;
}

export const ACCORDION_NAVIGATE_TO_ANCHOR_EVENT = 'accordion-navigate-to-anchor';

interface AccordionNavigateToAnchorDetail {
  readonly anchorId: string;
}

// Entries can come from independently-positioned CMS components (e.g. the buybox rating sits
// outside the accordion that owns the reviews entry), so a window CustomEvent is used to open
// the right entry and scroll to it without coupling the two components together.
export function navigateToAccordionAnchor(anchorId: string): void {
  window.dispatchEvent(
    new CustomEvent<AccordionNavigateToAnchorDetail>(ACCORDION_NAVIGATE_TO_ANCHOR_EVENT, {
      detail: { anchorId }
    })
  );
}

export interface Props {
  readonly id: string;
  readonly headline?: string | null;
  readonly subHeadline?: string | null;
  readonly headlineLink?: LinkComponentLink | null;
  readonly entries: ReadonlyArray<Entry>;
  readonly editorMode: boolean;
  readonly entriesMetadata: ReadonlyArray<Metadata>;
  readonly dateTo: number | null;
  readonly dateFrom: number | null;
}

const getInitialOpenedIndices = (entries: ReadonlyArray<Entry>): ReadonlyArray<number> =>
  entries
    .map((entry, index) => (entry.isInitialOpened ? index : -1))
    .filter((index) => index !== -1);

export default function AccordionComponent({
  id,
  headline,
  subHeadline,
  headlineLink,
  entries,
  editorMode,
  entriesMetadata,
  dateFrom,
  dateTo
}: Props): ReactElement {
  const { formatMessage }: NamespacedIntl = useNamespacedIntl('accordions');
  const [toggledIndices, setToggledIndices] = useState<ReadonlyArray<number>>(() =>
    getInitialOpenedIndices(entries)
  );
  const previousEntriesRef = useRef(entries);

  // toggledIndices is independently mutated by handleToggle, so it isn't purely derived from
  // entries; reset it post-paint (instead of during render) to avoid a synchronous re-render pass.
  useEffect(() => {
    if (previousEntriesRef.current !== entries) {
      previousEntriesRef.current = entries;
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
      setToggledIndices(getInitialOpenedIndices(entries));
    }
  }, [entries]);

  useEffect(() => {
    const handleNavigateToAnchor = (event: Event): void => {
      const { anchorId } = (event as CustomEvent<AccordionNavigateToAnchorDetail>).detail;
      const targetIndex = entries.findIndex((entry) => entry.anchorId === anchorId);
      if (targetIndex === -1) {
        return;
      }

      // Flushed synchronously so the collapsed styling clears before scrollIntoView measures
      // and scrolls to the (until now hidden) target element.
      flushSync(() => {
        setToggledIndices((prevToggledIndices) =>
          prevToggledIndices.includes(targetIndex)
            ? prevToggledIndices
            : [...prevToggledIndices, targetIndex]
        );
      });
      document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    window.addEventListener(ACCORDION_NAVIGATE_TO_ANCHOR_EVENT, handleNavigateToAnchor);
    return (): void =>
      window.removeEventListener(ACCORDION_NAVIGATE_TO_ANCHOR_EVENT, handleNavigateToAnchor);
  }, [entries]);

  const handleToggle = useCallback((index: number): void => {
    setToggledIndices((prevToggledIndices) =>
      prevToggledIndices.includes(index)
        ? prevToggledIndices.filter((i) => i !== index)
        : [...prevToggledIndices, index]
    );
  }, []);

  return (
    <>
      {editorMode && (
        <>
          <ConfigurableCmsSlotMetadata beforeSlot={true} metadata={entriesMetadata} />
          <ConfigurableCmsValidity dateFrom={dateFrom} dateTo={dateTo} />
        </>
      )}
      <div className={styles['accordion']}>
        {headline && (
          <HeadlineComponent
            id={`${id}-headline`}
            headline={headline}
            subHeadline={subHeadline ?? null}
            link={headlineLink ?? null}
            headlineTag={'h2'}
            className={styles['accordion__headline']}
          />
        )}
        {entries.map(
          (
            { title, content, headerAccessory, allowContentOverflow, metadata, dateFrom, dateTo },
            index
          ) => {
            const isToggled = toggledIndices.includes(index);
            const metadataSignature = metadata.map(({ key, value }) => `${key}:${value}`).join('|');
            return (
              <section
                key={metadataSignature || title}
                aria-label={`${title}`}
                className={classNames(styles['accordion-entry'], {
                  [styles['accordion-entry--collapsed']]: !isToggled
                })}
              >
                {editorMode && (
                  <>
                    <ConfigurableCmsComponentMetadata beforeComponent={true} metadata={metadata} />
                    <ConfigurableCmsValidity dateFrom={dateFrom} dateTo={dateTo} />
                  </>
                )}
                <button
                  className={styles['accordion-entry__label']}
                  aria-expanded={isToggled}
                  onClick={(): void => handleToggle(index)}
                >
                  <h3 className={'h3 bold'}>{title}</h3>
                  {headerAccessory && !isToggled && (
                    <span className={styles['accordion-entry__header-accessory']}>
                      {headerAccessory}
                    </span>
                  )}
                  <ImageComponent
                    className={styles['accordion-entry__expand-icon']}
                    src={'/assets/icons/dropdown-icon.svg'}
                    alt={formatMessage(isToggled ? `collapseIconAlt` : `expandIconAlt`)}
                    height={24}
                    width={24}
                  />
                </button>
                <div
                  className={classNames(styles['accordion-entry__content-wrapper'], {
                    [styles['accordion-entry__content-wrapper--allow-overflow']]:
                      allowContentOverflow
                  })}
                >
                  <div className={styles['accordion-entry__contents']}>
                    {typeof content === 'string' ? (
                      <div dangerouslySetInnerHTML={{ __html: content }} />
                    ) : (
                      content
                    )}
                  </div>
                </div>
                {editorMode && (
                  <ConfigurableCmsComponentMetadata beforeComponent={false} metadata={metadata} />
                )}
              </section>
            );
          }
        )}
      </div>
      {editorMode && <ConfigurableCmsSlotMetadata beforeSlot={false} metadata={entriesMetadata} />}
    </>
  );
}
